import React, { useMemo } from "react";
import {
	Bar,
	CartesianGrid,
	ComposedChart,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { formatAxisLabel, formatMetricValue } from "./partnerAnalyticsUtils";

const FALLBACK_COLOR = "#6B7280";

// Each partner's bar is filled with that partner's own solid colour. The
// Revenue/Payout/Registered breakdown behind a bar's height is only
// surfaced in the tooltip.
const BREAKDOWN_METRICS = [
	{ key: "revenue", label: "Revenue", valueType: "currency" },
	{ key: "payout", label: "Payout", valueType: "currency" },
	{ key: "registered", label: "Registered Users", valueType: "number" },
];

const fieldKey = (partnerId, metricKey) => `${partnerId}__${metricKey}`;
const totalKey = (partnerId) => `${partnerId}__total`;

const StackTooltip = ({ active, payload, label, partners, colorMap = {} }) => {
	if (!active || !payload?.length) return null;
	const row = payload[0]?.payload;

	return (
		<div
			style={{
				background: "#fff",
				border: "1px solid #E5E7EB",
				borderRadius: 8,
				padding: "10px 14px",
				fontSize: 13,
				boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
				maxWidth: 280,
			}}
		>
			<p style={{ fontWeight: 600, marginBottom: 8, color: "#111" }}>{label}</p>
			{partners.map((p) => {
				const rev = row?.[fieldKey(p.id, "revenue")];
				const pay = row?.[fieldKey(p.id, "payout")];
				const reg = row?.[fieldKey(p.id, "registered")];
				const cohortRevenue = row?.[`${p.id}__cohortRevenue`];
				const cohortPayout = row?.[`${p.id}__cohortPayout`];
				if (!rev && !pay && !reg) return null;
				const baseColor = colorMap[p.id] || FALLBACK_COLOR;

				return (
					<div key={p.id} style={{ marginBottom: 10 }}>
						<p style={{ margin: "0 0 4px 0", fontSize: 12, fontWeight: 700, color: baseColor }}>
							{p.name}
						</p>
						{BREAKDOWN_METRICS.map((m) => {
							const val = row?.[fieldKey(p.id, m.key)];
							if (val == null || val === 0) return null;
							return (
								<p key={m.key} style={{ color: "#4B5563", margin: "2px 0", fontSize: 12 }}>
									{m.label}: <strong>{formatMetricValue(val, m.valueType)}</strong>
								</p>
							);
						})}
						{cohortRevenue != null && cohortRevenue !== 0 && (
							<p style={{ color: baseColor, opacity: 0.75, margin: "2px 0 0 0", fontSize: 11 }}>
								↳ Cohort revenue: <strong>{formatMetricValue(cohortRevenue, "currency")}</strong>
							</p>
						)}
						{cohortPayout != null && cohortPayout !== 0 && (
							<p style={{ color: baseColor, opacity: 0.75, margin: "2px 0 0 0", fontSize: 11 }}>
								↳ Cohort payout: <strong>{formatMetricValue(cohortPayout, "currency")}</strong>
							</p>
						)}
					</div>
				);
			})}
		</div>
	);
};

/**
 * One separate column per partner, per date/month — coloured solely by
 * partner. The Revenue/Payout/Registered Users breakdown behind each bar's
 * height is shown in the tooltip on hover.
 */
const PartnerCandleChart = ({
	title = "Revenue · Payout · Registered Users",
	subtitle,
	icon = "📊",
	labels = [],
	revenueSeries = [],
	payoutSeries = [],
	registeredSeries = [],
	colorMap = {},
	mode,
	visiblePartnerIds = null,
	height = 340,
}) => {
	const partners = useMemo(() => {
		const byId = new Map();
		[...revenueSeries, ...payoutSeries, ...registeredSeries].forEach((s) => {
			if (!s?.id) return;
			if (visiblePartnerIds && !visiblePartnerIds.has(s.id)) return;
			if (!byId.has(s.id)) byId.set(s.id, { id: s.id, name: s.name });
		});
		return Array.from(byId.values());
	}, [revenueSeries, payoutSeries, registeredSeries, visiblePartnerIds]);

	const seriesMap = useMemo(() => {
		const map = { revenue: {}, payout: {}, registered: {} };
		revenueSeries.forEach((s) => { map.revenue[s.id] = s; });
		payoutSeries.forEach((s) => { map.payout[s.id] = s; });
		registeredSeries.forEach((s) => { map.registered[s.id] = s; });
		return map;
	}, [revenueSeries, payoutSeries, registeredSeries]);

	// One row per date — one separate column per partner (its solid colour),
	// height = the sum of Revenue + Payout + Registered Users for that
	// partner/date. The registered-users cohort's own revenue/totalPayout
	// breakdown (nested in that metric's payload) rides along tooltip-only.
	const chartData = useMemo(
		() =>
			labels.map((rawLabel, idx) => {
				const row = { name: formatAxisLabel(rawLabel, mode), _rawLabel: rawLabel };
				partners.forEach((p) => {
					const rev = seriesMap.revenue[p.id]?.data?.[idx] ?? 0;
					const pay = seriesMap.payout[p.id]?.data?.[idx] ?? 0;
					const reg = seriesMap.registered[p.id]?.data?.[idx] ?? 0;
					row[fieldKey(p.id, "revenue")] = rev;
					row[fieldKey(p.id, "payout")] = pay;
					row[fieldKey(p.id, "registered")] = reg;
					row[totalKey(p.id)] = (rev || 0) + (pay || 0) + (reg || 0);
					row[`${p.id}__cohortRevenue`] = seriesMap.registered[p.id]?.extra?.revenue?.[idx] ?? null;
					row[`${p.id}__cohortPayout`] =
						seriesMap.registered[p.id]?.extra?.totalPayout?.[idx] ??
						seriesMap.registered[p.id]?.extra?.total_payout?.[idx] ??
						null;
				});
				return row;
			}),
		[labels, partners, seriesMap, mode]
	);

	const hasAnyData = chartData.some((row) =>
		partners.some((p) => {
			const rev = row[fieldKey(p.id, "revenue")];
			const pay = row[fieldKey(p.id, "payout")];
			const reg = row[fieldKey(p.id, "registered")];
			return rev || pay || reg;
		})
	);

	const partnerCount = partners.length;
	const barSize = partnerCount <= 1 ? 40 : partnerCount <= 3 ? 22 : partnerCount <= 6 ? 14 : 10;
	const angled = labels.length > 14;

	return (
		<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
			<div style={{ marginBottom: 8 }}>
				<h3 style={{ margin: 0 }}>
					{icon ? <span style={{ marginRight: 8 }}>{icon}</span> : null}
					{title}
				</h3>
				{subtitle && (
					<p style={{ fontSize: 13, color: "var(--text-muted)", margin: "4px 0 0 0" }}>{subtitle}</p>
				)}
			</div>

			{!hasAnyData ? (
				<div style={{ padding: "48px 0", textAlign: "center", color: "var(--text-muted)", fontSize: 14 }}>
					No data available for the selected filters.
				</div>
			) : (
				<ResponsiveContainer width="100%" height={height}>
					<ComposedChart
						data={chartData}
						margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
						barCategoryGap={partnerCount > 1 ? "18%" : "28%"}
						barGap={partnerCount > 1 ? 2 : 4}
					>
						<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
						<XAxis
							dataKey="name"
							tick={{ fontSize: 12 }}
							angle={angled ? -35 : 0}
							textAnchor={angled ? "end" : "middle"}
							height={angled ? 52 : 30}
							interval="preserveStartEnd"
							minTickGap={24}
						/>
						<YAxis tick={{ fontSize: 12 }} width={48} />
						<Tooltip content={<StackTooltip partners={partners} colorMap={colorMap} />} />
						<Legend
							wrapperStyle={{ fontSize: 12 }}
							payload={partners.map((p) => ({
								value: p.name,
								type: "square",
								color: colorMap[p.id] || FALLBACK_COLOR,
								id: totalKey(p.id),
							}))}
						/>
						{partners.map((p) => (
							<Bar
								key={totalKey(p.id)}
								dataKey={totalKey(p.id)}
								name={p.name}
								fill={colorMap[p.id] || FALLBACK_COLOR}
								maxBarSize={barSize}
							/>
						))}
					</ComposedChart>
				</ResponsiveContainer>
			)}
		</div>
	);
};

export default PartnerCandleChart;
